import { useMutation, useQuery } from '@apollo/client'
import { Alert, SecondaryButton } from '@designsforhealth/dfh-react-components'
import { CircularProgress } from '@material-ui/core'
import { format } from 'date-fns'
import { GraphQLError } from 'graphql'
import { all, always, compose, has, or, propIs, test, tryCatch } from 'ramda'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'

import {
  AddSimpleProductsToCartDocument,
  AddSimpleProductsToCartMutation,
} from '../../../../graphql/magento'
import { SearchProductBySkuDocument } from '../../../../graphql/search'
import useLogAndCaptureError from '../../../../hooks/useLogAndCaptureError'
import useUserCategory from '../../../../hooks/useUserCategory'
import { TokenContext } from '../../../../layouts/context'
import { publishAddToCartEventForItems } from '../../../../lib/analytics/cart'
import { useCartContext } from '../../../../lib/cart'
import { useAddToCartToast } from '../../../../lib/cart/hooks/useAddToCartToast'
import { resolveItemQuantityConfig } from '../../../../lib/cart/item-control-options'
import { useDispensaryCartStatus } from '../../../../lib/dispensaries/events/cart'
import { SortedItem } from '../../../../lib/orders/context'
import { useOrderItemReorderIneligibility } from '../../../../lib/orders/hooks/useOrderItemReorderIneligibility'
import { MagentoProductsQuerySimpleProduct } from '../../../../lib/products'
import useSanityImage from '../../../../lib/sanity/hooks/useSanityImage'
import { logAndCaptureException } from '../../../../utils/errorTools'
import { tapSuccessfulQueryResponse } from '../../../../utils/graphqlTools'
import {
  maxItemOrderQuantityByUserCategory,
  reorderItemQuantity,
} from '../../../../utils/itemTools'
import { itemQuantityPrice } from '../../../../utils/priceTools'
import Link from '../../../global/Link'
import * as Styled from './order/OrderItemEntry/styled'

import productPlaceholderImage from '../../../../img/product-image-placeholder.png'

const isStringIfPresent = (obj: Record<string, unknown>) => (prop: string) =>
  or(!has(prop, obj), propIs(String, prop, obj))

interface ProductAttributeValues {
  size?: string
  dosage?: string
  flavor?: string
}

function isProductAttributeValues(value: unknown): value is ProductAttributeValues {
  if (!value || typeof value !== 'object') return false

  return all(isStringIfPresent(value as Record<string, unknown>), ['size', 'dosage', 'flavor'])
}

const isProductNotAvailableError = compose(
  test(/Product that you are trying to add is not available/),
  (obj: GraphQLError) => obj.message,
)

export interface ReorderItemsEntryProps {
  className?: string
  item: SortedItem & { product: MagentoProductsQuerySimpleProduct }
}

const parseProductAttributes = compose(
  (val: unknown): ProductAttributeValues => (isProductAttributeValues(val) ? val : {}),
  tryCatch(
    (item: MagentoProductsQuerySimpleProduct) =>
      item.variant_index?.attribute_values ? JSON.parse(item.variant_index.attribute_values) : {},
    always({}),
  ),
)

const ReorderItemsEntry: React.FC<ReorderItemsEntryProps> = ({ className, item }) => {
  const { loading: dispensaryCartLoading } = useDispensaryCartStatus()
  const token = useContext(TokenContext)
  const { cart } = useCartContext()
  const { userCategory } = useUserCategory()
  const { showSuccessToast } = useAddToCartToast()
  const itemIneligibleForReorder = useOrderItemReorderIneligibility(item.sku)

  const isCartItemMaxOrderQtyExceeded = useMemo(() => {
    const maxItemOrderQtyByUserCategory = maxItemOrderQuantityByUserCategory(userCategory)
    if (!maxItemOrderQtyByUserCategory) {
      return false
    }
    const cartItem = cart?.items?.find((itemData) => itemData?.product?.sku === item.product.sku)
    const currentCartItemQuantity = cartItem?.quantity ?? 0
    const { maxQuantityAllowedInCart } = resolveItemQuantityConfig(
      cartItem?.product?.stock_configuration,
    )
    /*
    If the item configuration's max cart quantity is less than the default override value,
    honor the configuration's value instead.
    */
    const maxItemOrderQuantity =
      maxQuantityAllowedInCart < maxItemOrderQtyByUserCategory
        ? maxQuantityAllowedInCart
        : maxItemOrderQtyByUserCategory
    return maxItemOrderQuantity <= currentCartItemQuantity
  }, [cart?.items, item.product.sku, userCategory])

  const onCompleted = useCallback(
    (data: AddSimpleProductsToCartMutation) => {
      if (data?.addSimpleProductsToCart) {
        showSuccessToast({ skus: [item.sku] })
      }
    },
    [item.sku, showSuccessToast],
  )

  const [addSimpleProductsToCart, { loading: addToCartLoading, error }] = useMutation(
    AddSimpleProductsToCartDocument,
    {
      context: { token },
      onCompleted,
    },
  )

  useLogAndCaptureError(error)

  const handleReorder = useMemo(() => {
    const cartId = cart?.id
    const sku = item.sku
    if (!cartId || !sku) {
      return undefined
    }

    const reorderQuantity = reorderItemQuantity({
      minSaleQty: item.product.stock_configuration?.min_sale_qty,
    })

    return () =>
      addSimpleProductsToCart({
        variables: {
          cartId,
          cartItems: [
            {
              data: {
                sku,
                quantity: reorderQuantity,
                dfh_order_type: 1,
                dfh_autoship_interval: 1,
              },
            },
          ],
        },
      }).then(
        tapSuccessfulQueryResponse(
          publishAddToCartEventForItems({
            items: [
              {
                quantity: reorderQuantity,
                sku,
              },
            ],
          }),
        ),
      )
  }, [addSimpleProductsToCart, cart?.id, item.product.stock_configuration?.min_sale_qty, item.sku])

  const productAttributes = parseProductAttributes(item.product)

  const errorMessage = useMemo(() => {
    if (!error) return undefined

    const catchableError = error.graphQLErrors.find(isProductNotAvailableError)
    if (!catchableError) {
      logAndCaptureException(error)
      return undefined
    }
    return catchableError.message
  }, [error])

  const { data } = useQuery(SearchProductBySkuDocument, {
    variables: {
      sku: item.product.sku ?? '',
    },
    context: { token, uri: process.env.GATSBY_SEARCH_URL },
    skip: !item.product.sku,
  })

  const product = data?.productBySku

  const sanityImageUrl = useSanityImage(
    { sourceJson: product?.mainImage?.json },
    {
      dpr: 2,
      fit: 'max',
      // set width only to avoid crop bug: https://github.com/sanity-io/image-url/issues/32
      width: 180,
    },
  )

  return (
    <div className={className}>
      <Styled.GapContainer>
        <Styled.ItemDetails>
          <Link to={`/products/${item.product.url_key}`}>
            <Styled.ItemImage
              alt={item.product.image?.label ?? ''}
              src={sanityImageUrl || productPlaceholderImage}
            />
          </Link>
          <div>
            <Styled.ItemProductLink urlKey={item.product.url_key}>
              <Styled.ItemName>{item.product.name}</Styled.ItemName>
            </Styled.ItemProductLink>
            {productAttributes.size && (
              <Styled.ItemAttribute>Size: {productAttributes.size}</Styled.ItemAttribute>
            )}
            {productAttributes.dosage && (
              <Styled.ItemAttribute>Dosage: {productAttributes.dosage}</Styled.ItemAttribute>
            )}
            {productAttributes.flavor && (
              <Styled.ItemAttribute>Flavor: {productAttributes.flavor}</Styled.ItemAttribute>
            )}
            <Styled.ItemAttribute>Last Purchased: {format(item.date, 'P')}</Styled.ItemAttribute>
            <Styled.ItemAmount>
              Price: {itemQuantityPrice({ item: item.product, qty: 1 })}
            </Styled.ItemAmount>
          </div>
        </Styled.ItemDetails>
        <Styled.ButtonContainer>
          <Styled.AddToCartButton
            onClick={handleReorder}
            disabled={
              addToCartLoading ||
              dispensaryCartLoading ||
              itemIneligibleForReorder ||
              isCartItemMaxOrderQtyExceeded
            }
          >
            {addToCartLoading ? <CircularProgress size={16} color="inherit" /> : 'Reorder Item'}
          </Styled.AddToCartButton>
          <SecondaryButton as={Link} to={`/products/${item.product.url_key}`}>
            View Product Page
          </SecondaryButton>
        </Styled.ButtonContainer>
      </Styled.GapContainer>
      {error && (
        <Alert type="error">{errorMessage || 'Could not add product to shopping cart.'}</Alert>
      )}
    </div>
  )
}

const StyledReorderItemsEntry = styled(ReorderItemsEntry)`
  background-color: ${({ theme }) => theme.colors.grayscale.white};
  overflow: hidden;
`

export { StyledReorderItemsEntry as ReorderItemsEntry }
